/**
 * Waitlist OFFER service — CLIENT side (Jose 2026-08-06)
 *
 * The client opens their texted claim link (/claim/:token), sees a 5-minute countdown, and taps
 * one of the offered slots. This service reads the offer by token and CLAIMS it: it books the
 * appointment (with the shared double-book guard — first-come-first-served, so if a walk-in grabbed
 * it first the claim fails cleanly), marks the offer 'claimed', and marks the waitlist entry booked.
 *
 * Shared brain: shared/waitlistOffer.ts (types + expiry logic).
 */
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { bookingService } from './bookingService';
import { WaitlistOffer, OFFER_HOLD_MINUTES, offerClaimable } from '../shared/waitlistOffer';

const COLLECTION = 'waitlist_offers';

function mapOffer(id: string, data: any): WaitlistOffer {
  return {
    id,
    token: data.token || id,
    entryId: data.entryId,
    clientId: data.clientId,
    clientName: data.clientName,
    clientPhone: data.clientPhone,
    clientEmail: data.clientEmail,
    options: data.options || [],
    status: data.status || 'sent',
    createdAtMs: data.createdAtMs ?? data.createdAt?.toMillis?.() ?? 0,
    expiresAtMs: data.expiresAtMs ?? data.expiresAt?.toMillis?.() ?? 0,
    holdMinutes: data.holdMinutes ?? OFFER_HOLD_MINUTES,
    claimedOption: data.claimedOption,
    claimedAtMs: data.claimedAtMs,
    mode: data.mode,
    appointmentId: data.appointmentId,
  };
}

class WaitlistOfferService {
  /** Load an offer by its token (== doc id). Returns null if it doesn't exist. */
  async getOfferByToken(token: string): Promise<WaitlistOffer | null> {
    const snap = await getDoc(doc(db, COLLECTION, token));
    if (!snap.exists()) return null;
    return mapOffer(snap.id, snap.data());
  }

  /**
   * Claim option #index from an offer. Books the appointment (double-book guarded), then marks the
   * offer claimed + the waitlist entry booked. Throws a friendly error if the offer expired or the
   * slot was just taken.
   */
  async claimOffer(offer: WaitlistOffer, index: number): Promise<{ time: string; date: string; barberName: string }> {
    if (!offerClaimable(offer, Date.now())) {
      throw new Error('This offer has expired. Please contact the shop for a new time.');
    }
    const opt = offer.options[index];
    if (!opt) throw new Error('That option is no longer available.');

    if (offer.mode === 'reschedule' && offer.appointmentId) {
      // RESCHEDULE: MOVE the existing appointment (guard runs inside) — no duplicate, same id/history.
      await bookingService.rescheduleAppointment(offer.appointmentId, opt.date, opt.time, opt.barberId, opt.barberName);
    } else {
      // Books through the shared guard — if a walk-in grabbed the slot first, this throws.
      await bookingService.createAppointment({
        clientId: offer.clientId || `waitlist-${offer.entryId}`,
        clientName: offer.clientName,
        clientPhone: offer.clientPhone,
        clientEmail: offer.clientEmail || '',
        barberId: opt.barberId,
        barberName: opt.barberName,
        service: opt.service,
        date: opt.date,
        time: opt.time,
        duration: opt.duration,
        price: opt.price,
        status: 'confirmed',
        source: 'online',
        reminderSent: false,
      } as any);
    }

    await updateDoc(doc(db, COLLECTION, offer.id), {
      status: 'claimed',
      claimedOption: index,
      claimedAtMs: Date.now(),
      claimedAt: Timestamp.now(),
    });

    // Waitlist bookkeeping only applies to a real waitlist entry (not a reschedule of an appointment).
    if (offer.mode !== 'reschedule') {
      try {
        await updateDoc(doc(db, 'waitlist', offer.entryId), { status: 'booked' });
      } catch (e) {
        console.warn('Could not mark waitlist entry booked (non-fatal):', e);
      }
    }

    return { time: opt.time, date: opt.date, barberName: opt.barberName };
  }
}

export const waitlistOfferService = new WaitlistOfferService();
export default waitlistOfferService;
